import { useEffect, useState } from "react";
import { StreamVideoClient } from "@stream-io/video-react-sdk";
import { StreamChat } from "stream-chat";
import { api, formatApiErrorDetail } from "@/lib/api";

// Fetches server-issued GetStream credentials (the backend re-checks ownership/membership
// every time, including token refreshes) and connects Video + Chat clients.
export function useLiveSession(endpoint, { viewer = false } = {}) {
  const [state, setState] = useState({ status: "connecting" });
  useEffect(() => {
    let alive = true;
    let video;
    let call;
    let chat;
    const fetchCreds = async () => (await api.post(endpoint)).data;
    (async () => {
      try {
        const creds = await fetchCreds();
        const tokenProvider = async () => (await fetchCreds()).token;
        const user = { id: creds.user_id, name: creds.user_name };
        video = new StreamVideoClient({ apiKey: creds.api_key, user, token: creds.token, tokenProvider });
        call = video.call(creds.call_type, creds.call_id);
        if (viewer) {
          await call.camera.disable().catch(() => {});
          await call.microphone.disable().catch(() => {});
        }
        if (!alive) { video.disconnectUser().catch(() => {}); return; }
        await call.join();
        // Unmounted while joining (e.g. React StrictMode double effect): leave so no ghost participant inflates viewer counts.
        if (!alive) { call.leave().catch(() => {}); video.disconnectUser().catch(() => {}); return; }
        chat = new StreamChat(creds.api_key);
        await chat.connectUser(user, creds.token);
        const channel = chat.channel(creds.channel_type, creds.channel_id);
        await channel.watch();
        if (alive) setState({ status: "ready", creds, video, call, chat, channel });
      } catch (e) {
        const code = e.response?.status;
        if (alive) setState({ status: "error", code, error: e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message });
      }
    })();
    return () => {
      alive = false;
      call?.leave().catch(() => {});
      video?.disconnectUser().catch(() => {});
      chat?.disconnectUser().catch(() => {});
    };
  }, [endpoint, viewer]);
  return state;
}
